// Canonical vocabulary for delivery apps and other store integrations.
//
// Values sent to and received from the API are always the canonical keys
// below; Arabic/English names are display labels only. The backend keeps an
// equivalent copy in lud-backend/constants/integrations.js, and
// lud-backend/test/integrations.test.js fails when the two drift apart.

// Delivery apps with report analysis today.
export const DELIVERY_APPS = Object.freeze(["hungerstation", "jahez", "marsool", "ninja", "keeta", "toyou"]);

// Recognized delivery apps without analysis/report support yet.
export const FUTURE_DELIVERY_APPS = Object.freeze(["thechefz", "noonfood", "shgardi", "mrmandoob"]);

// Store integrations that are not delivery apps.
export const OTHER_INTEGRATIONS = Object.freeze(["LUD", "pos", "feedus"]);

export const ALL_DELIVERY_APPS = Object.freeze([...DELIVERY_APPS, ...FUTURE_DELIVERY_APPS]);
export const ALL_INTEGRATIONS = Object.freeze([...ALL_DELIVERY_APPS, ...OTHER_INTEGRATIONS]);

// Options offered when configuring a branch: delivery apps plus the branch's
// integration (FeedUs) link.
export const BRANCH_INTEGRATIONS = Object.freeze([...ALL_DELIVERY_APPS, "feedus"]);

export const INTEGRATION_LABELS = Object.freeze({
  hungerstation: Object.freeze({ key: "hungerstation", en: "HungerStation", ar: "هنقرستيشن" }),
  jahez: Object.freeze({ key: "jahez", en: "Jahez", ar: "جاهز" }),
  marsool: Object.freeze({ key: "marsool", en: "Marsool", ar: "مرسول" }),
  ninja: Object.freeze({ key: "ninja", en: "Ninja", ar: "نينجا" }),
  keeta: Object.freeze({ key: "keeta", en: "Keeta", ar: "كيتا" }),
  toyou: Object.freeze({ key: "toyou", en: "ToYou", ar: "تويو" }),
  thechefz: Object.freeze({ key: "thechefz", en: "The Chefz", ar: "ذا شفز" }),
  noonfood: Object.freeze({ key: "noonfood", en: "Noon Food", ar: "نون فود" }),
  shgardi: Object.freeze({ key: "shgardi", en: "Shgardi", ar: "شقردي" }),
  mrmandoob: Object.freeze({ key: "mrmandoob", en: "Mr Mandoob", ar: "مستر مندوب" }),
  LUD: Object.freeze({ key: "LUD", en: "LUD", ar: "LUD" }),
  pos: Object.freeze({ key: "pos", en: "POS", ar: "POS" }),
  feedus: Object.freeze({ key: "feedus", en: "FeedUs Integration", ar: "ربط فيداس" }),
});

// Historical spellings, so data written before the canonical vocabulary still
// resolves to its key. Matching is exact first, then case/space/punctuation
// insensitive.
export const INTEGRATION_ALIASES = Object.freeze({
  hungerstation: ["HungerStation", "Hungerstation", "Hunger Station", "hunger", "Hunger", "هنقرستيشن", "هنقر ستيشن", "هنقر", "هنجر"],
  jahez: ["Jahez", "jahiz", "جاهز"],
  marsool: ["Marsool", "mrsool", "Mrsool", "MRSOOL", "mrsool direct", "marsool direct", "مرسول"],
  ninja: ["Ninja", "نينجا"],
  keeta: ["Keeta", "كيتا"],
  toyou: ["ToYou", "Toyou", "To You", "toyo", "تويو", "تو يو"],
  thechefz: ["TheChefz", "The Chefz", "Chefz", "ذا شفز", "ذاشفز", "ذا شيفز"],
  noonfood: ["NoonFood", "Noon Food", "نون فود"],
  shgardi: ["Shgardi", "شقردي"],
  mrmandoob: ["mrMandoob", "mr.Mandoob", "Mr Mandoob", "مستر مندوب"],
  LUD: ["lud"],
  pos: ["POS"],
  feedus: ["FeedUs", "Feedus", "فيداس", "فييد اس", "Integration", "integration", "التكامل"],
});

const fold = (value) =>
  String(value ?? "")
    .normalize("NFKC")
    .trim()
    .replace(/[إأآٱ]/g, "ا")
    .replace(/ى/g, "ي")
    .replace(/ة/g, "ه")
    .replace(/ـ/g, "")
    .toLowerCase()
    .replace(/[\s._-]+/g, "");

const EXACT_LOOKUP = new Map();
const FOLDED_LOOKUP = new Map();
ALL_INTEGRATIONS.forEach((key) => {
  [key, ...(INTEGRATION_ALIASES[key] || [])].forEach((spelling) => {
    EXACT_LOOKUP.set(spelling, key);
    FOLDED_LOOKUP.set(fold(spelling), key);
  });
});

const DELIVERY_SET = new Set(ALL_DELIVERY_APPS);

// Canonical key for a known spelling, or null when unknown (never guessed).
export function normalizeIntegrationKey(value) {
  if (value === null || value === undefined) return null;
  const raw = String(value).trim();
  if (!raw) return null;
  return EXACT_LOOKUP.get(raw) || FOLDED_LOOKUP.get(fold(raw)) || null;
}

// Canonical delivery-app key, or null (unknown values and LUD/pos/feedus).
export function normalizeDeliveryAppKey(value) {
  const key = normalizeIntegrationKey(value);
  return key && DELIVERY_SET.has(key) ? key : null;
}

export const isDeliveryApp = (value) => normalizeDeliveryAppKey(value) !== null;

// Display label for a key (or a legacy spelling); unknown values are shown as
// they are.
export function integrationLabel(value, lang = "en") {
  const key = normalizeIntegrationKey(value);
  if (!key) return value === null || value === undefined ? "" : String(value);
  return INTEGRATION_LABELS[key][lang === "ar" ? "ar" : "en"];
}

// { value, label } options for a select, values being canonical keys.
export const integrationOptions = (keys, lang = "en") =>
  keys.map((key) => ({ value: key, label: integrationLabel(key, lang) }));

// Expands an object keyed by canonical keys so every known spelling of a key
// resolves to the same value.
export function expandIntegrationKeys(byKey) {
  const map = {};
  Object.entries(byKey).forEach(([key, value]) => {
    [key, ...(INTEGRATION_ALIASES[key] || [])].forEach((spelling) => {
      map[spelling] = value;
    });
  });
  return map;
}

// Label lookup object keyed by canonical key and every known spelling, for
// code that indexes a translation map with a stored value.
export const integrationLabelMap = (lang = "en", keys = ALL_INTEGRATIONS) =>
  expandIntegrationKeys(Object.fromEntries(keys.map((key) => [key, integrationLabel(key, lang)])));
